import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import img from "./all_bg.jpg";

const BACKEND_URL = "https://api.bbsfacultyprofile.com";
const TABLE_NAME = "industry_experience";

const IndustryExperience = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [formData, setFormData] = useState({
    starting_month_year: null,
    ending_month_year: null,
    position: "",
    institution: "",
    nature_of_experience: "",
    industry_name: "",
    country: "",
  });
  const [recordId, setRecordId] = useState(null);

  // Fetch records
  const fetchRecords = async () => {
  try {
    const res = await fetch(`${BACKEND_URL}/api/${TABLE_NAME}`, {
      method: "GET",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Failed to fetch records");

    const data = await res.json();

    const processed = data
      .map((rec) => ({
        ...rec,
        starting_month_year: rec.starting_month_year
          ? new Date(rec.starting_month_year)
          : null,
        ending_month_year: rec.ending_month_year
          ? new Date(rec.ending_month_year)
          : null,
      }))
      .sort((a, b) => {
        if (!a.starting_month_year) return 1;
        if (!b.starting_month_year) return -1;

        return (
          b.starting_month_year.getTime() -
          a.starting_month_year.getTime()
        );
      });

    setRecords(processed);
  } catch (err) {
    console.error(err);
    toast.error(err.message);
  }
};

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      starting_month_year: formData.starting_month_year
        ? `${formData.starting_month_year.getFullYear()}-${String(
            formData.starting_month_year.getMonth() + 1
          ).padStart(2, "0")}-01`
        : null,

      ending_month_year: formData.ending_month_year
        ? `${formData.ending_month_year.getFullYear()}-${String(
            formData.ending_month_year.getMonth() + 1
          ).padStart(2, "0")}-01`
        : null,
    };

    try {
      const url = recordId
        ? `${BACKEND_URL}/api/${TABLE_NAME}/${recordId}`
        : `${BACKEND_URL}/api/${TABLE_NAME}`;
      const method = recordId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save record");

      toast.success("Record saved successfully!");
      setFormData({
        starting_month_year: null,
        ending_month_year: null,
        position: "",
        institution: "",
        nature_of_experience: "",
        industry_name: "",
        country: "",
      });
      setRecordId(null);
      fetchRecords();
    } catch (err) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      starting_month_year: record.starting_month_year
        ? new Date(record.starting_month_year)
        : null,
      ending_month_year: record.ending_month_year
        ? new Date(record.ending_month_year)
        : null,
      position: record.position,
      institution: record.institution,
      nature_of_experience: record.nature_of_experience,
      industry_name: record.industry_name,
      country: record.country,
    });
    setRecordId(record.id);
    toast.info("Editing record...");
  };

  const handleDelete = (record) => {
    const toastId = toast.info(
      <div>
        Delete this record?{" "}
        <button
          onClick={async () => {
            try {
              const res = await fetch(
                `${BACKEND_URL}/api/${TABLE_NAME}/${record.id}`,
                {
                  method: "DELETE",
                  credentials: "include",
                }
              );
              if (!res.ok) throw new Error("Failed to delete record");

              fetchRecords();
              toast.update(toastId, {
                render: "Record deleted successfully!",
                type: "success",
                autoClose: 3000,
                closeButton: true,
              });
            } catch (err) {
              toast.update(toastId, {
                render: `Delete error: ${err.message}`,
                type: "error",
                autoClose: 3000,
                closeButton: true,
              });
            }
          }}
          style={{
            marginLeft: "10px",
            padding: "3px 8px",
            background: "#ef4444",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Confirm
        </button>
      </div>,
      { autoClose: false, closeOnClick: false }
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return format(new Date(dateStr), "MM/yyyy");
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 20px",
        fontFamily: "Arial, sans-serif",
        backgroundImage: `url(${img})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <ToastContainer position="top-center" autoClose={3000} />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          background: "#fff",
          padding: "30px 40px",
          borderRadius: "10px",
          boxShadow: "0px 0px 12px rgba(0,0,0,0.1)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            color: "#1e3a8a",
            marginBottom: "25px",
          }}
        >
          Industry Experience
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Dates Row */}
          <div style={{ display: "flex", gap: "10px" }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Starting Month/Year (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.starting_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, starting_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: "bold" }}>
                Ending Month/Year (mm/yyyy)
              </label>
              <DatePicker
                selected={formData.ending_month_year}
                onChange={(date) =>
                  setFormData({ ...formData, ending_month_year: date })
                }
                dateFormat="MM/yyyy"
                showMonthYearPicker
                placeholderText="Select month/year"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Other fields */}
          {[
            { label: "Position", name: "position", type: "text" },
            { label: "Institution", name: "institution", type: "text" },
            { label: "Industry Name", name: "industry_name", type: "text" },
            { label: "Country", name: "country", type: "text" },
          ].map((field) => (
            <div key={field.name} style={{ marginTop: "10px" }}>
              <label style={{ fontWeight: "bold" }}>{field.label}</label>
              <input
                type="text"
                name={field.name}
                value={formData[field.name]}
                onChange={handleChange}
                required
                style={{
                  width: "100%",
                  padding: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "6px",
                  marginTop: "5px",
                }}
              />
            </div>
          ))}

          {/* Nature of Experience Dropdown */}
          <div style={{ marginTop: "10px" }}>
            <label style={{ fontWeight: "bold" }}>Nature of Experience</label>
            <select
              name="nature_of_experience"
              value={formData.nature_of_experience}
              onChange={handleChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "6px",
                marginTop: "5px",
              }}
            >
              <option value="">Select Nature of Experience</option>
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
            </select>
          </div>

          <button
            type="submit"
            style={{
              background: "#3b82f6",
              color: "white",
              padding: "12px",
              border: "none",
              borderRadius: "6px",
              marginTop: "20px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {recordId ? "Update Record" : "Add Record"}
          </button>
        </form>

        {/* Records Table */}
        {records.length > 0 && (
          <div style={{ marginTop: "30px", overflowX: "auto" }}>
            <h3 style={{ color: "#1e3a8a" }}>
              Your Industry Experience Records
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                marginTop: "10px",
              }}
            >
              <thead>
                <tr>
                  {[
                    "Start",
                    "End",
                    "Position",
                    "Institution",
                    "Nature of Experience",
                    "Industry Name",
                    "Country",
                    "Actions",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        background: "#f1f5f9",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((rec) => (
                  <tr key={rec.id}>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.starting_month_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {formatDate(rec.ending_month_year)}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.position}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.institution}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.nature_of_experience}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.industry_name}
                    </td>
                    <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                      {rec.country}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        gap: "5px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(rec)}
                        style={{
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rec)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          borderRadius: "5px",
                          padding: "5px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "25px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "#1e3a8a",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default IndustryExperience;
